import type React from 'react';
import type { PropsWithChildren } from 'react';
import { useCallback, useState } from 'react';

import type { EmptyProps } from '@change-corgi/core/types';
import { createMandatoryContext } from '@change-corgi/core-react/context';

import { useOnBfcacheRestore } from '#src/app/shared/hooks/lifecycle';

type PromoteSlideUpFlow = {
	/**
	 * The in-flight or settled treat + `$exposure` round-trip per petition. The footer and the
	 * share-success redirect both trigger the flow, and the supporter must be treated once.
	 */
	treatments: Map<string, Promise<boolean>>;
	/**
	 * Petitions the automatic share redirect must no longer act on, because a direct tap already
	 * sent the supporter somewhere.
	 */
	claimedNavigations: Set<string>;
	/** Petitions whose sheet has had its one showing; a later tap navigates on instead. */
	shownSheets: Set<string>;
	/**
	 * `${key}:${reason}` pairs already reported to Sentry, so a slow or degraded experiment-service
	 * call reports once per petition per page mount rather than once per tap.
	 */
	reportedIssues: Set<string>;
	/**
	 * How many times the page has come back from the bfcache. A continuation that started before a
	 * restore belongs to a visit the supporter already left, and must not act when it resumes.
	 */
	restores: RestoreCounter;
};

type RestoreCounter = {
	count: () => number;
	bump: () => void;
};

function createRestoreCounter(): RestoreCounter {
	let restores = 0;

	return {
		count: () => restores,
		bump: () => {
			restores += 1;
		},
	};
}

function createPromoteSlideUpFlow(): PromoteSlideUpFlow {
	return {
		treatments: new Map(),
		claimedNavigations: new Set(),
		shownSheets: new Set(),
		reportedIssues: new Set(),
		restores: createRestoreCounter(),
	};
}

const { Provider, useContext: usePromoteSlideUpFlowContext } = createMandatoryContext<PromoteSlideUpFlow>(undefined, {
	name: 'PsfSharePromoteSlideUpFlowContext',
});

export const PromoteSlideUpFlowProvider: React.FC<PropsWithChildren<EmptyProps>> = ({ children }) => {
	// Lazy initializer rather than a ref, so the object is created once, never during render, and
	// keeps a stable identity across every re-render.
	const [flow] = useState(createPromoteSlideUpFlow);

	// A restored page keeps the state it had when the supporter left, which would leave the footer
	// dead: its claim spent and its sheet already counted as shown.
	useOnBfcacheRestore(
		useCallback(() => {
			flow.claimedNavigations.clear();
			flow.shownSheets.clear();
			flow.restores.bump();
		}, [flow]),
	);

	return <Provider value={flow}>{children}</Provider>;
};

/**
 * Page-scoped state for the Second Promote Ask R1 flow. Scoped to the page mount rather than the
 * module so a client-side navigation back to the share page starts clean.
 */
export const usePromoteSlideUpFlow = usePromoteSlideUpFlowContext;
